import AddressStatus from "enums/AddressStatus";
import State from "enums/State";

export interface FormikAddress {
  addressName?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  defaultLocation?: boolean;
  geocode?: {
    x: number; // Longitude
    y: number; // Latitude
  };
}

export const addressInitialValues: FormikAddress = {
  addressName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "",
  defaultLocation: false,
  geocode: undefined,
};

export function mapAddressToFormik(addressData: any): FormikAddress {
  if (!addressData) return { ...addressInitialValues };

  return {
    addressName: addressData.addressName ?? "",
    address1: addressData.address1 ?? "",
    address2: addressData.address2 ?? "",
    city: addressData.city ?? "",
    state: addressData.state ?? "",
    zipcode: addressData.zipcode ?? "",
    country: addressData.country ?? "",
    defaultLocation: addressData.defaultLocation ?? false,
    geocode: addressData.geocode ?? undefined,
  };
}

export function buildUpdateAddressVariables(
  addressId: string,
  formData: FormikAddress,
) {
  return {
    id: addressId,
    input: {
      addressName: formData.addressName,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipcode?.toString(),
      country: formData.country,
    },
  };
}

export function buildAddAddressVariables(
  companyId: string,
  formData: FormikAddress,
) {
  return {
    input: {
      companyId,
      addressName: formData.addressName,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipcode?.toString(),
      country: formData.country,
      status: AddressStatus.ACTIVE,
      defaultLocation: formData.defaultLocation,
    },
  };
}
export const stateOptions = [
  { label: "Alabama", value: State.AL },
  { label: "Alaska", value: State.AK },
  { label: "Arizona", value: State.AZ },
  { label: "Arkansas", value: State.AR },
  { label: "California", value: State.CA },
  { label: "Colorado", value: State.CO },
  { label: "Connecticut", value: State.CT },
  { label: "Delaware", value: State.DE },
  { label: "Florida", value: State.FL },
  { label: "Georgia", value: State.GA },
  { label: "Hawaii", value: State.HI },
  { label: "Idaho", value: State.ID },
  { label: "Illinois", value: State.IL },
  { label: "Indiana", value: State.IN },
  { label: "Iowa", value: State.IA },
  { label: "Kansas", value: State.KS },
  { label: "Kentucky", value: State.KY },
  { label: "Louisiana", value: State.LA },
  { label: "Maine", value: State.ME },
  { label: "Maryland", value: State.MD },
  { label: "Massachusetts", value: State.MA },
  { label: "Michigan", value: State.MI },
  { label: "Minnesota", value: State.MN },
  { label: "Mississippi", value: State.MS },
  { label: "Missouri", value: State.MO },
  { label: "Montana", value: State.MT },
  { label: "Nebraska", value: State.NE },
  { label: "Nevada", value: State.NV },
  { label: "New Hampshire", value: State.NH },
  { label: "New Jersey", value: State.NJ },
  { label: "New Mexico", value: State.NM },
  { label: "New York", value: State.NY },
  { label: "North Carolina", value: State.NC },
  { label: "North Dakota", value: State.ND },
  { label: "Ohio", value: State.OH },
  { label: "Oklahoma", value: State.OK },
  { label: "Oregon", value: State.OR },
  { label: "Pennsylvania", value: State.PA },
  { label: "Rhode Island", value: State.RI },
  { label: "South Carolina", value: State.SC },
  { label: "South Dakota", value: State.SD },
  { label: "Tennessee", value: State.TN },
  { label: "Texas", value: State.TX },
  { label: "Utah", value: State.UT },
  { label: "Vermont", value: State.VT },
  { label: "Virginia", value: State.VA },
  { label: "Washington", value: State.WA },
  { label: "West Virginia", value: State.WV },
  { label: "Wisconsin", value: State.WI },
  { label: "Wyoming", value: State.WY },
  { label: "District of Columbia", value: State.DISTRICT_OF_COLOMBIA },
  { label: "Puerto Rico", value: State.PR },
];

export const incorporationStateOptions = stateOptions
  .filter((option) => option.value !== State.PR)
  .map((option) =>
    option.value === State.DISTRICT_OF_COLOMBIA
      ? { ...option, value: "DISTRICT_OF_COLOMBIA" }
      : option,
  );

export const countryOptions = [{ label: "United States", value: "US" }];

/** The parts of an address the one-line display reads; every caller's address type fits it. */
export interface AddressLineParts {
  addressName?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}

/**
 * The single line an address field shows: its name, which `ConfigureAddress`
 * fills from the parts when no custom name is asked for, or the parts
 * themselves for an address saved without one.
 */
export function formatAddressLine(
  address: AddressLineParts | null | undefined,
): string {
  const name = address?.addressName?.trim();
  if (name) return name;
  if (!address?.address1) return "";
  const street = [address.address1, address.address2, address.city]
    .filter(Boolean)
    .join(", ");
  const region = [address.state, address.zipcode].filter(Boolean).join(" ");
  return [street, region].filter(Boolean).join(", ");
}

export function getStateFullName(abbreviation: string): string {
  const found = stateOptions.find((opt) => opt.value === abbreviation);
  return found ? found.label : abbreviation;
}

export function getCountryFullName(abbreviation: string): string {
  const found = countryOptions.find((opt) => opt.value === abbreviation);
  return found ? found.label : abbreviation;
}

export enum ConfigureAddressValidationType {
  VALIDATE = "validate",
  CREATE = "create",
}
